// src/components/common/SettingsDrawer.tsx
// Slide-in settings panel accessible via the ⚙ gear icon in the app header.
// Sections: Appearance (theme), Voice (TTS settings).
import { useEffect, useLayoutEffect, useRef } from "react";
import type { ThemePreference } from "@/hooks/useTheme";
import type { SpeakMode, VoiceProvider } from "@/lib/speech/voiceSettings";
import { useAuthOptional } from "@/contexts/AuthContext";
import type { OrgLocation } from "@/config/locations";
import type { Location } from "@/types/location";
import { DevImpersonationSettingsSection } from "@/components/common/DevImpersonationSettingsSection";
import { SettingsDrawerAppearanceSection } from "./SettingsDrawerAppearanceSection";
import { SettingsDrawerLeadsSection } from "./SettingsDrawerLeadsSection";
import { SettingsDrawerProfileSection } from "./SettingsDrawerProfileSection";
import { SettingsDrawerVoiceSection } from "./SettingsDrawerVoiceSection";
import { useSettingsDrawerAccount } from "./useSettingsDrawerAccount";

export interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  theme: ThemePreference;
  setTheme: (t: ThemePreference) => void;
  voiceEnabled: boolean;
  setVoiceEnabled: (v: boolean) => void;
  voiceProvider: VoiceProvider;
  setVoiceProvider: (v: VoiceProvider) => void;
  speakMode: SpeakMode;
  setSpeakMode: (m: SpeakMode) => void;
  showLeadsControls: boolean;
  emptyLeadAutoDeleteEnabled: boolean;
  setEmptyLeadAutoDeleteEnabled: (v: boolean) => void;
  syncedCount: number;
  viableCount: number;
  isSyncing: boolean;
  onSyncAllLeads: () => Promise<void>;
  location: Location | null;
  selectableLocations: OrgLocation[];
  setLocation: (l: Location) => void;
  locationsLoaded: boolean;
}

export function SettingsDrawer({
  open,
  onClose,
  theme,
  setTheme,
  voiceEnabled,
  setVoiceEnabled,
  voiceProvider,
  setVoiceProvider,
  speakMode,
  setSpeakMode,
  showLeadsControls,
  emptyLeadAutoDeleteEnabled,
  setEmptyLeadAutoDeleteEnabled,
  syncedCount,
  viableCount,
  isSyncing,
  onSyncAllLeads,
  location,
  selectableLocations,
  setLocation,
  locationsLoaded,
}: SettingsDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);
  const auth = useAuthOptional();
  const account = useSettingsDrawerAccount(auth);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const onMouse = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) onClose();
    };
    const id = window.setTimeout(() => document.addEventListener("mousedown", onMouse), 50);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("mousedown", onMouse);
    };
  }, [open, onClose]);

  useLayoutEffect(() => {
    if (open) return;
    const root = drawerRef.current;
    if (!root) return;
    const active = document.activeElement;
    if (active instanceof HTMLElement && root.contains(active)) {
      active.blur();
    }
  }, [open]);

  return (
    <>
      {open ? <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px] transition-opacity" /> : null}

      <div
        ref={drawerRef}
        className={
          "fixed top-0 right-0 h-full w-80 max-w-full z-50 shadow-2xl flex flex-col " +
          "bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 " +
          "transform transition-transform duration-300 ease-in-out " +
          (open ? "translate-x-0" : "translate-x-full pointer-events-none")
        }
        role={open ? "dialog" : undefined}
        aria-modal={open ? true : undefined}
        aria-labelledby={open ? "settings-drawer-title" : undefined}
        inert={!open}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 id="settings-drawer-title" className="text-base font-semibold">
            Settings
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close settings"
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {auth?.session && auth.profile ? (
            <SettingsDrawerProfileSection
              auth={auth}
              account={account}
              showLeadsControls={showLeadsControls}
              location={location}
              selectableLocations={selectableLocations}
              setLocation={setLocation}
              locationsLoaded={locationsLoaded}
            />
          ) : null}

          <SettingsDrawerAppearanceSection theme={theme} setTheme={setTheme} />

          <SettingsDrawerVoiceSection
            voiceEnabled={voiceEnabled}
            setVoiceEnabled={setVoiceEnabled}
            voiceProvider={voiceProvider}
            setVoiceProvider={setVoiceProvider}
            speakMode={speakMode}
            setSpeakMode={setSpeakMode}
          />

          {showLeadsControls ? (
            <SettingsDrawerLeadsSection
              emptyLeadAutoDeleteEnabled={emptyLeadAutoDeleteEnabled}
              setEmptyLeadAutoDeleteEnabled={setEmptyLeadAutoDeleteEnabled}
              syncedCount={syncedCount}
              viableCount={viableCount}
              isSyncing={isSyncing}
              onSyncAllLeads={onSyncAllLeads}
            />
          ) : null}

          {auth ? <DevImpersonationSettingsSection containerOpen={open} auth={auth} /> : null}
        </div>
      </div>
    </>
  );
}
